import { makeAutoObservable } from "mobx";
import { ZIdName } from "src/types/ZIdName";
import { ZObjectItem } from "src/types/ZObjectItem";
import { ZUid } from "src/types/zUid";
import { loadObjectSummaries } from "../api/objects";
import { ObjectNodeData, objectNode } from "./node";
import { actionsOf } from "./permissions";
import { TreeStore } from "./tree";

const sameId = (a: ZUid, b: ZUid) => String(a) === String(b);

/**
 * Каталог объектов: лёгкий список, из которого строятся корни дерева.
 *
 * Полные данные объекта здесь не хранятся - за ними отдельный ресурс.
 * Порядок задаёт каталог: сортировка зависит от языка интерфейса, поэтому
 * язык приходит функцией, а не импортом appStore в домен.
 */
export class CatalogStore {
  private items: ObjectNodeData[] = [];

  constructor(
    private readonly tree: TreeStore,
    private readonly language: () => string,
  ) {
    makeAutoObservable<CatalogStore, "language" | "tree">(this, {
      tree: false,
      language: false,
    });
  }

  get summaries(): readonly ObjectNodeData[] {
    return this.items;
  }

  /** Для выпадающих списков: раскрывать объекты ради имён не нужно. */
  get options(): ZIdName[] {
    return this.items.map(({ id, name }) => ({ id, name }));
  }

  async load() {
    this.replace(await loadObjectSummaries());
  }

  /** Объект появился или изменился: создан, скопирован, импортирован, переименован. */
  upsert(data: ObjectNodeData) {
    this.replace([...this.items.filter((it) => !sameId(it.id, data.id)), data]);
  }

  remove(objectId: ZUid) {
    this.replace(this.items.filter((it) => !sameId(it.id, objectId)));
  }

  /** Данные каталога по полному объекту: имя и права могли измениться. */
  // eslint-disable-next-line class-methods-use-this
  fromDetails(details: ZObjectItem, prev?: ObjectNodeData): ObjectNodeData {
    return {
      id: details.id,
      // detail-ответ отдаёт displayId как nullish: терять известное значение нельзя
      displayId: details.displayId ?? prev?.displayId ?? null,
      name: details.name,
      canUpdate: actionsOf(details).update,
      canDelete: actionsOf(details).delete,
    };
  }

  find(objectId: ZUid): ObjectNodeData | undefined {
    return this.items.find((it) => sameId(it.id, objectId));
  }

  clear() {
    this.items = [];
    this.tree.setRoots([]);
  }

  private replace(next: ObjectNodeData[]) {
    const language = this.language();
    const sorted = [...next].sort((a, b) =>
      a.name.localeCompare(b.name, language, { sensitivity: "base" }),
    );
    this.items = sorted;
    this.tree.setRoots(sorted.map(objectNode));
  }
}
